import { z } from 'zod'

import {
  BookingPaymentType,
  BookingStatus,
  BookingType,
  BookingUserPaymentStatus,
  BookingUserStatus,
  BookingUserType,
  CreditType,
} from '../constants/booking'
import { InventoryType } from '../constants/inventory'
import {
  BookingFeeType,
  BookingDiscountAudience,
  BookingDiscountType,
} from '../constants/pricing'
import {
  listQueryZodFields,
  dbEntitySchema,
  submitterContextSchema,
} from './system'
import { feeSchema } from './pricing'
import { MembershipLevelValue, MembershipStatus } from '../constants/membership'
import { AttributionType } from '../constants/attribution'
import { StripeConnectType } from '../constants/venue'
import { userSchema } from './user'
import { prospectSchema } from './prospect'
import { venueSchema } from './venue'
import { eventSchema } from './event'
import { inventorySchema } from './inventory'
import { trackerSchema } from './tracker'
import { affiliateSchema } from './affiliate'
import { referralSchema } from './referral'
import { paymentInstrumentSnapshotSchema } from './paymentInstrument'

export const orderItemSchema = z.object({
  _id: z.string(),
  inventory: z.string().nullish(),
  productId: z.string().nullish(),
  date: z.date().nullish(),
  level: z.string().nullish(),
  name: z.string().nullish(),
  brand: z.string().nullish(),
  type: z.nativeEnum(InventoryType).nullish(),
  guests: z.number().nullish(),
  arrivalTime: z.date().nullish(),
  price: z.number().nullish(),
  description: z.string().nullish(),
  quantity: z.number(),
  membershipLevel: z.number().nullish(),
  discountValue: z.number().nullish(),
  paymentType: z.nativeEnum(BookingPaymentType).nullish(),
  depositAmount: z.number().nullish(),
  reserved: z.boolean().nullish(),
})

export const bookingDiscountSchema = z.object({
  _id: z.string(),
  id: z.string(),
  promo: z.string().nullish(),
  discount: z.string().nullish(),
  userDiscount: z.string().nullish(),
  name: z.string(),
  amount: z.number().min(0),
  amountType: z.nativeEnum(BookingDiscountType),
  type: z.nativeEnum(BookingDiscountAudience),
  value: z.number().min(0),
  quantityApplied: z.number().min(0),
  // quantityUsed: z.number().nullish(),
  quantityAppliedMax: z.number().nullish(),
})

export const bookingUserSchema = z.object({
  _id: z.string(),
  id: z.string(),
  created: z.date().nullish(),
  modified: z.date().nullish(),
  email: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  gender: z.string().nullish(),
  phone: z.string().nullish(),
  membershipStatus: z.string().nullish(),
  membershipLevel: z.nativeEnum(MembershipLevelValue).nullish(),
  bookingCount: z.number().or(z.string().nullish()),
  imageUrl: z.string().nullish(),
  name: z.string().nullish(),
  user: z.string().nullish(),
  prospect: z.string().nullish(),
  status: z.nativeEnum(BookingUserStatus).nullish(),
  paymentStatus: z.nativeEnum(BookingUserPaymentStatus).nullish(),
  amount: z.number().nullish(),
  type: z.nativeEnum(BookingUserType).nullish(),
  paymentProfile: z.string().nullish(),
  transaction: z.string().nullish(),
  paymentFailed: z.boolean().nullish(),
  token: z.string().nullish(),
})

export const bookingFeeSchema = feeSchema.extend({
  feeType: z.nativeEnum(BookingFeeType).nullish(),
})

const bookingBreakdownProps = {
  payment: z.nativeEnum(BookingPaymentType).nullish(),
  currencyCode: z.string().nullish(),
  paymentProviderAccountId: z.string().nullish(),
  paymentProviderChargeId: z.string().nullish(),
  paymentProviderSettlementCurrencyCode: z.string().nullish(),
  paymentProviderExchangeRate: z.number().nullish(),
  paymentProviderChargeUsd: z.number().nullish(),
  paymentProviderProcessingFee: z.number().nullish(),
  paymentProviderProcessingFeeUsd: z.number().nullish(),
  paymentProviderApplicationFee: z.number().nullish(),
  paymentProviderApplicationFeeUsd: z.number().nullish(),
  stripeConnectType: z.nativeEnum(StripeConnectType).nullish(),
  fees: z.array(bookingFeeSchema).nullish(),
  discounts: z.array(bookingDiscountSchema).nullish(),
  total: z.number().nullish(),
  totalWithCredit: z.number().nullish(),
  totalWithoutCredit: z.number().nullish(),
  totalWithDiscounts: z.number().nullish(),
  totalWithoutFees: z.number().nullish(),
  feeTotal: z.number().nullish(),
  venueFeeTotal: z.number().nullish(),
  tablelistFeeTotal: z.number().nullish(),
  processingFeeTotal: z.number().nullish(),
  dueOnArrival: z.number().nullish(),
  price: z.number().nullish(),
  minimum: z.number().nullish(),
  additionalSpend: z.number().nullish(),
  taxAndTip: z.number().nullish(),
  tax: z.number().nullish(),
  taxPercent: z.number().nullish(),
  tip: z.number().nullish(),
  tipPercent: z.number().nullish(),
  processingFees: z.array(bookingFeeSchema).nullish(),
  processingFeePercent: z.number().nullish(),
}

export const bookingBreakdownSchema = z.object({
  ...bookingBreakdownProps,
})

export const bookingSchema = dbEntitySchema.extend({
  ...bookingBreakdownProps,
  id: z.string(),
  _id: z.string(),
  type: z.nativeEnum(BookingType),
  created: z.date().nullish(),
  voided: z.boolean().nullish(),
  status: z.nativeEnum(BookingStatus),
  lastStatus: z.nativeEnum(BookingStatus).nullish(),
  date: z.date(),
  completeNotificationsSent: z.boolean().nullish(),
  name: z.string().nullish(),
  location: z.string().nullish(),
  discountTotal: z.number().nullish(),
  attributionId: z.string().nullish(),
  attributionType: z.nativeEnum(AttributionType).nullish(),
  attributionName: z.string().nullish(),
  attribution: referralSchema.partial().nullish(),
  referral: referralSchema.partial().nullish(),
  hasTickets: z.boolean().nullish(),
  refund: z
    .object({
      amount: z.number().nullish(),
      reason: z.string().nullish(),
      user: z.string().nullish(),
    })
    .nullish(),
  /**
   * @deprecated - not sure what this is for??
   */
  priceNet: z.number().nullish(),
  notes: z
    .object({
      internal: z.string().nullish(),
      venue: z.string().nullish(),
      user: z.string().nullish(),
    })
    .optional()
    .nullable(),
  sale: z.string().nullish(),
  admissionGroup: z.string().nullish(),
  venue: z.string(),
  event: z.string().nullish(),
  organization: z.string(),
  city: z.string().nullish(),
  user: z.string().nullish(),
  primaryUser: z
    .object({
      _id: z.string(),
      id: z.string(),
      user: z.string().nullish(),
      firstName: z.string().nullish(),
      lastName: z.string().nullish(),
      name: z.string().nullish(),
      email: z.string().nullish(),
      phone: z.string().nullish(),
      imageUrl: z.string().nullish(),
      membershipStatus: z.string().nullish(),
      bookingCount: z.number().nullish(),
      gender: z.string().nullish(),
    })
    .partial()
    .nullish(),
  users: z.array(bookingUserSchema),
  prospect: z.string().nullish(),
  rewards: z.array(z.string()).nullish(),
  affiliate: z.string().nullish(),
  tracker: z.string().nullish(),
  items: z.array(orderItemSchema).nullish(),
  stripeMetadataUpdated: z.boolean().nullish(),
  syncedToBraze: z.boolean().nullish(),
  client: z.string().nullish(),
  userMembershipLevel: z.number().nullish(),
  customerMarketingOptIn: z.boolean().nullish(),
  customerMarketingOptInSignature: z.string().nullish(),
  submitter: submitterContextSchema.nullish(),
  transaction: z.string().nullish(),
  arrivalTime: z.date().nullish(),
  completeDate: z.date().nullish(),
  arrivalStartTime: z.date().nullish(),
  arrivalEndTime: z.date().nullish(),
  guests: z.number().nullish(),
  quantity: z.number().nullish(),
  inventoryType: z.nativeEnum(InventoryType),
  /**
   * @deprecated - use item.inventory instead
   */
  inventory: z.string().nullish(),
  /**
   * @deprecated - use item.level instead
   */
  inventoryLevel: z.string().nullish(),
  /**
   * @deprecated - use item.membershipLevel instead
   */
  inventoryMembershipLevel: z.number().nullish(),
  inventoryPrice: z.number().nullish(),
  /**
   * @deprecated - use item.description instead
   */
  inventoryDescription: z.string().nullish(),
  //
  confirmationRequest: z.string().nullish(),
  reservation: z.boolean().nullish(),
  ticketToken: z.string().nullish(),
  paymentDetail: paymentInstrumentSnapshotSchema.nullish(),
  paymentProviderCheckoutSession: z
    .object({
      chargeId: z.string().nullish(),
      sessionId: z.string().nullish(),
      paymentIntentId: z.string().nullish(),
      setupIntentId: z.string().nullish(),
    })
    .nullish(),
  // additionalSpend: z.number().nullish(),
})

export const bookingsListQuerySchema = z.object({
  ...listQueryZodFields,
  id: z.string().or(z.array(z.string())).nullish(),
  search: z.string().nullish(),
  dateStart: z.coerce.date().nullish(),
  dateEnd: z.coerce.date().nullish(),
  status: z
    .nativeEnum(BookingStatus)
    .or(z.array(z.nativeEnum(BookingStatus)))
    .nullish(),
  inventoryType: z
    .nativeEnum(InventoryType)
    .or(z.array(z.nativeEnum(InventoryType)))
    .nullish(),
  organizationId: z.string().or(z.array(z.string())).nullish(),
  venueId: z.string().or(z.array(z.string())).nullish(),
  eventId: z.string().nullish(),
  cityId: z.string().nullish(),
  affiliateId: z.string().nullish(),
  trackingId: z.string().nullish(),
  paymentType: z
    .nativeEnum(BookingPaymentType)
    .or(z.array(z.nativeEnum(BookingPaymentType)))
    .nullish(),
})

export const bookingsListResponseSchema = z.object({
  data: z.array(bookingSchema),
})

export const bookingCreateItemInputSchema = z
  .object({
    inventoryId: z.string().optional(),
    productId: z.string().optional(),
    date: z.coerce.date().optional(),
    quantity: z.number().optional(),
    reserved: z.boolean().optional(),
    arrivalTime: z.coerce.date().nullish(),
    guests: z.number().int().min(1).nullish(),
  })
  .superRefine((val, ctx) => {
    if (val.inventoryId) return
    if (val.productId && val.date) return
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'inventoryId or productId+date is required',
    })
  })

export const bookingCreateArgsSchema = z
  .object({
    // venueId: z.string(),
    date: z.coerce.date().optional(),

    customerData: z
      .object({
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
        email: z.string().nullish(),
        phone: z.string().nullish(),
      })
      .nullish(),

    items: z.array(bookingCreateItemInputSchema).min(1),
    user: userSchema.nullish(),
    prospect: prospectSchema.nullish(),
    client: z.string().optional(),
    arrivalTime: z.coerce.date().optional(),
    tip: z.number().optional(),
    rewards: z.array(z.string()).optional(),
    payment: z.nativeEnum(BookingPaymentType).optional(),
    paymentProfileId: z.string().optional(),
    type: z.string().optional(),
    paymentType: z.nativeEnum(BookingPaymentType).optional(),
    attributionId: z.string().optional(),
    status: z.nativeEnum(BookingStatus).optional(),

    eventId: z.string().optional(),
    affiliateId: z.string().optional(),
    trackingId: z.string().optional(),
    name: z.string().optional(),
    customerMarketingOptIn: z.boolean().optional(),
    customerMarketingOptInSignature: z.string().optional(),
  })
  .refine((data) => data.user || data.prospect, {
    message: 'user or prospect is required',
  })

export const bookingAttributionUpdateSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(AttributionType),
})

export const bookingUpdateByIdArgsSchema = z
  .object({
    name: z.string().optional(),
    arrivalTime: z.coerce.date().optional(),
    guests: z.number().optional(),
    notes: z
      .object({
        venue: z.string().optional(),
        internal: z.string().optional(),
      })
      .optional(),
    userNotes: z.string().optional(),
    customerMarketingOptIn: z.boolean().optional(),
    customerMarketingOptInSignature: z.string().optional(),
    attribution: bookingAttributionUpdateSchema.optional(),
    date: z.coerce.date().optional(),
    userId: z.string().optional(),
    prospectId: z.string().optional(),
    credit: z.unknown().optional(),
  })
  .partial()

export const orderCreateFromCartArgsSchema = z.object({
  cartId: z.string(),
  orderId: z.string().optional(),
  client: z.string().optional(),
  attributionId: z.string().optional(),
  items: z
    .array(
      z.object({
        inventoryId: z.string(),
        arrivalTime: z.coerce.date().nullish(),
        guests: z.number().int().min(1).nullish(),
      })
    )
    .optional(),
  name: z.string().optional(),
  arrivalTime: z.coerce.date().optional(),
  userNotes: z.string().optional(),
  customerMarketingOptIn: z.boolean().optional(),
  customerMarketingOptInSignature: z.string().optional(),
  savePaymentMethod: z.boolean().optional(),
})

export const orderCreatePaymentArgsSchema = z.object({
  savePaymentMethod: z.boolean().optional(),
})

export const orderCreateFromCartResponseSchema = z.object({
  order: bookingSchema,
  clientSecret: z.string(),
  customerSessionClientSecret: z.string().nullish(),
  publishableKey: z.string(),
  stripeAccountId: z.string().nullable(),
  mode: z.enum(['payment', 'setup']),
  amount: z.number(),
  currency: z.string(),
})

export const orderCreatePaymentResponseSchema = z.object({
  orderId: z.string(),
  clientSecret: z.string(),
  customerSessionClientSecret: z.string().nullish(),
  publishableKey: z.string(),
  stripeAccountId: z.string().nullable(),
  mode: z.enum(['payment', 'setup']),
  amount: z.number(),
  currency: z.string(),
})

export const orderCompletePaymentArgsSchema = z
  .object({
    paymentIntentId: z.string().optional(),
    setupIntentId: z.string().optional(),
  })
  .refine(
    (args) =>
      (Boolean(args.paymentIntentId) && !args.setupIntentId) ||
      (!args.paymentIntentId && Boolean(args.setupIntentId)),
    {
      message: 'exactly one of paymentIntentId or setupIntentId is required',
    }
  )

export const validatedBookingCreateItemSchema = z.object({
  inventoryId: z.string().nullish(),
  productId: z.string().nullish(),
  date: z.date().nullish(),
  quantity: z.number(),
  reserved: z.boolean().nullish(),
  arrivalTime: z.date().nullish(),
  guests: z.number().int().min(1).nullish(),
  inventory: inventorySchema.passthrough(),
})

export const bookingValidateCreateResultSchema = z.object({
  date: z.date(),
  items: z.array(validatedBookingCreateItemSchema),
  venue: venueSchema,
  event: eventSchema.nullable(),
  paymentProfile: z.record(z.unknown()).nullable(),
  affiliate: affiliateSchema.nullable(),
  tracker: trackerSchema.nullable(),
})

export const bookingValidateCancelArgsSchema = z.object({
  id: z.string(),
  date: z.date(),
  status: z.nativeEnum(BookingStatus),
})

export const bookingValidateCompleteOptionsSchema = z.object({
  user: userSchema.optional(),
  prospect: prospectSchema.optional(),
  email: z.string().optional(),
})

export const bookingDisputeEvidenceSchema = z.object({
  service_date: z.string(),
  access_activity_log: z.string(),
  customer_purchase_ip: z.string(),
  customer_name: z.string().optional(),
  customer_email_address: z.string().optional(),
  product_description: z.string(),
  cancellation_policy: z.string(),
  cancellation_policy_disclosure: z.string(),
  refund_policy: z.string(),
  refund_policy_disclosure: z.string(),
  refund_refusal_explanation: z.string().optional(),
  uncategorized_file: z.string(),
})

export const bookingServiceListOptionsSchema = z.object({
  useSearch: z.boolean().optional(),
  skip: z.number().optional(),
  limit: z.number().optional(),
  sort: z.string().optional(),
})

export const stripeFileUploadSchema = z.object({
  id: z.string(),
})

export const bookingRefundByIdArgsSchema = z.object({
  reason: z.string(),
  user: z
    .object({
      id: z.string().optional(),
      admin: z.boolean().optional(),
    })
    .optional(),
})

export const bookingListByUserIdQuerySchema = z.object({
  status: z.array(z.nativeEnum(BookingStatus)).optional(),
  inventoryType: z.array(z.nativeEnum(InventoryType)).optional(),
  userStatus: z.nativeEnum(BookingUserStatus).optional(),
  start: z.coerce.date().optional(),
  end: z.coerce.date().optional(),
  after: z.string().optional(),
  sort: z.string().optional(),
  limit: z.number().optional(),
  populate: z.unknown().optional(),
  select: z.string().optional(),
})

export const bookingChargeUserByIdArgsSchema = z.object({
  bookingId: z.string(),
  userId: z.string(),
})

export const bookingUpdateUserPaymentArgsSchema = z
  .object({
    bookingId: z.string(),
    userId: z.string().optional(),
    prospectId: z.string().optional(),
    paymentProfileId: z.string(),
  })
  .refine((data) => Boolean(data.userId || data.prospectId), {
    message: 'userId or prospectId is required',
  })

export const bookingGeneratePassbookArgsSchema = z
  .object({
    bookingId: z.string(),
    ticketId: z.string().optional(),
    ticketToken: z.string().optional(),
  })
  .refine((data) => Boolean(data.ticketId || data.ticketToken), {
    message: 'ticketId or ticketToken is required',
  })

export const bookingUserUpdateEmailArgsSchema = z
  .object({
    bookingId: z.string(),
    userId: z.string().optional(),
    prospectId: z.string().optional(),
    email: z.string(),
  })
  .refine((data) => Boolean(data.userId || data.prospectId), {
    message: 'userId or prospectId is required',
  })

export const bookingPaymentCreateTransactionArgsSchema = z
  .object({
    amount: z.number(),
    bookingId: z.string(),
    userId: z.string().optional(),
    prospectId: z.string().optional(),
    paymentProfileId: z.string(),
  })
  .refine((data) => Boolean(data.userId || data.prospectId), {
    message: 'userId or prospectId is required',
  })

export const bookingStatusUpdatedEventPayloadSchema = z.object({
  bookingId: z.string(),
  status: z.nativeEnum(BookingStatus),
})
